import { useSupabase } from "../hooks/useSupabase";
import { Post, PostCategory, CreatePostData, UpdatePostData, PostWithCategories, LanguageCode, TranslationInput } from "../types/post";

export const usePostService = () => {
  const supabase = useSupabase();

  // Check if translation system is available
  const checkTranslationSystemAvailable = async (): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("translations")
        .select("id")
        .limit(1);
      return !error;
    } catch {
      return false;
    }
  };

  // Check which columns exist in category_post table
  const checkCategoryPostSchema = async () => {
    try {
      // Use a safe approach: select only id first to see if table exists
      const { error } = await supabase
        .from("category_post")
        .select("id")
        .limit(1);
      
      if (error) {
        // Table doesn't exist or other error
        return {
          hasName: false,
          hasGroupKey: false,
          hasIcon: false,
          hasColor: false,
          hasDescription: false
        };
      }
      
      // Table exists, now try select * to see available columns
      // but handle gracefully if it fails
      try {
        const { data: allData, error: allError } = await supabase
          .from("category_post")
          .select("*")
          .limit(1);
        
        if (!allError && allData && allData.length > 0) {
          const sampleRecord = allData[0];
          return {
            hasName: 'name' in sampleRecord,
            hasGroupKey: 'group_key' in sampleRecord,
            hasIcon: 'icon' in sampleRecord,
            hasColor: 'color' in sampleRecord,
            hasDescription: 'description' in sampleRecord
          };
        }
      } catch (selectAllError) {
        console.warn("Could not determine full schema, using safe defaults");
      }
      
      // Safe fallback - assume only id exists (which we know works)
      return {
        hasName: false,
        hasGroupKey: false,
        hasIcon: false,
        hasColor: false,
        hasDescription: false
      };
    } catch {
      // Complete fallback
      return {
        hasName: false,
        hasGroupKey: false,
        hasIcon: false,
        hasColor: false,
        hasDescription: false
      };
    }
  };

  // Translation helper functions
  const translationHelpers = {
    async getRecordTranslations(tableName: string, recordId: string) {
      const { data, error } = await supabase
        .from("translations")
        .select("*")
        .eq("table_name", tableName)
        .eq("record_id", recordId);
      
      if (error) throw error;
      return data || [];
    },

    async setRecordTranslations(tableName: string, recordId: string, translations: TranslationInput[]) {
      const promises = translations.map(translation =>
        supabase.rpc('set_translation', {
          p_table_name: tableName,
          p_record_id: recordId,
          p_field_name: translation.field_name,
          p_language_code: translation.language_code,
          p_value: translation.value
        })
      );
      
      await Promise.all(promises);
    },

    async deleteRecordTranslations(tableName: string, recordId: string) {
      const { error } = await supabase
        .from("translations")
        .delete()
        .eq("table_name", tableName)
        .eq("record_id", recordId);
      
      if (error) throw error;
    },

    formatTranslations(translations: any[]) {
      const result: Record<string, Record<LanguageCode, string>> = {};
      
      translations.forEach(translation => {
        if (!result[translation.field_name]) {
          result[translation.field_name] = {} as Record<LanguageCode, string>;
        }
        (result[translation.field_name] as any)[translation.language_code] = translation.value;
      });
      
      return result;
    },

    getLocalizedText(
      translations: Record<LanguageCode, string> | undefined,
      language: LanguageCode = 'pl',
      fallback: LanguageCode = 'pl'
    ): string {
      if (!translations) return '';
      
      if (translations[language]) {
        return translations[language];
      }
      
      if (language !== fallback && translations[fallback]) {
        return translations[fallback];
      }
      
      const availableLanguages: LanguageCode[] = ['pl', 'en', 'uk'];
      for (const lang of availableLanguages) {
        if (translations[lang]) {
          return translations[lang];
        }
      }
      
      return '';
    }
  };

  return {
    // Get all posts with their categories
    async getPosts(): Promise<PostWithCategories[]> {
      const { data, error } = await supabase
        .from("post")
        .select(`
          *,
          post_category(
            category_post(
              id
            )
          )
        `)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      
      // Get all categories with their translations
      const categories = await this.getPostCategories('pl');
      const categoryMap = new Map(categories.map(cat => [cat.id, cat.name || cat.id]));
      
      return data.map((post: any) => {
        const categoryIds = post.post_category?.map((pc: any) => pc.category_post.id) || [];
        const categoryNames = categoryIds.map((id: string) => categoryMap.get(id) || id);
        
        return {
          ...post,
          category_names: categoryNames
        };
      });
    },

    // Get a single post with categories
    async getPost(id: string): Promise<PostWithCategories | null> {
      const { data, error } = await supabase
        .from("post")
        .select(`
          *,
          post_category(
            category_post(
              id
            )
          )
        `)
        .eq("id", id)
        .single();
      
      if (error) {
        if (error.code === "PGRST116") {
          return null;
        }
        throw error;
      }
      
      // Get all categories with their translations
      const categories = await this.getPostCategories('pl');
      const categoryMap = new Map(categories.map(cat => [cat.id, cat.name || cat.id]));
      
      const categoryIds = data.post_category?.map((pc: any) => pc.category_post.id) || [];
      const categoryNames = categoryIds.map((id: string) => categoryMap.get(id) || id);
      
      return {
        ...data,
        category_names: categoryNames
      };
    },

    // Get all post categories with translations (fallback to old system)
    async getPostCategories(language: LanguageCode = 'pl'): Promise<PostCategory[]> {
      const translationSystemAvailable = await checkTranslationSystemAvailable();
      const schema = await checkCategoryPostSchema();
      
      if (translationSystemAvailable) {
        // New translation system - only select ID since name/description are in translations
        const { data, error } = await supabase
          .from("category_post")
          .select("id, group_key, icon, color");
        
        if (error) throw error;
        
        const categoriesWithTranslations: PostCategory[] = await Promise.all(
          data.map(async (category) => {
            const translations = await translationHelpers.getRecordTranslations('category_post', category.id);
            const formattedTranslations = translationHelpers.formatTranslations(translations);
            
            return {
              ...category,
              name: translationHelpers.getLocalizedText(formattedTranslations.name, language),
              description: translationHelpers.getLocalizedText(formattedTranslations.description, language),
              translations: {
                name: formattedTranslations.name || {},
                description: formattedTranslations.description || {}
              }
            };
          })
        );
        
        return categoriesWithTranslations;
      } else {
        // Fallback to old system - dynamically build select query based on available columns
        let selectColumns = ["id"];
        if (schema.hasName) selectColumns.push("name");
        if (schema.hasGroupKey) selectColumns.push("group_key");
        if (schema.hasIcon) selectColumns.push("icon");
        if (schema.hasColor) selectColumns.push("color");
        if (schema.hasDescription) selectColumns.push("description");
        
        const { data, error } = await supabase
          .from("category_post")
          .select(selectColumns.join(", "));
        
        if (error) throw error;
        return (data || []) as unknown as PostCategory[];
      }
    },

    // Create a new post category (with fallback)
    async createPostCategory(
      nameOrTranslations: string | { name: Record<LanguageCode, string>; description?: Record<LanguageCode, string> },
      groupKey?: string, 
      icon?: string, 
      color?: string,
      description?: string
    ): Promise<PostCategory> {
      const translationSystemAvailable = await checkTranslationSystemAvailable();
      const schema = await checkCategoryPostSchema();
      
      if (translationSystemAvailable && typeof nameOrTranslations === 'object') {
        // New translation system - only insert non-translatable fields
        const insertData: any = {};
        if (schema.hasGroupKey && groupKey) insertData.group_key = groupKey;
        if (schema.hasIcon && icon) insertData.icon = icon;
        if (schema.hasColor && color) insertData.color = color;
        
        const { data, error } = await supabase
          .from("category_post")
          .insert(insertData)
          .select()
          .single();
        
        if (error) throw error;
        
        const translations = nameOrTranslations;
        const translationInputs: TranslationInput[] = [];
        
        Object.entries(translations.name).forEach(([lang, value]) => {
          if (value.trim()) {
            translationInputs.push({
              field_name: 'name',
              language_code: lang as LanguageCode,
              value: value.trim()
            });
          }
        });
        
        if (translations.description) {
          Object.entries(translations.description).forEach(([lang, value]) => {
            if (value.trim()) {
              translationInputs.push({
                field_name: 'description',
                language_code: lang as LanguageCode,
                value: value.trim()
              });
            }
          });
        }
        
        if (translationInputs.length > 0) {
          await translationHelpers.setRecordTranslations('category_post', data.id, translationInputs);
        }
        
        const categoryTranslationsData = await translationHelpers.getRecordTranslations('category_post', data.id);
        const categoryTranslations = translationHelpers.formatTranslations(categoryTranslationsData);
        
        return {
          ...data,
          name: translationHelpers.getLocalizedText(categoryTranslations.name, 'pl'),
          description: translationHelpers.getLocalizedText(categoryTranslations.description, 'pl'),
          translations: {
            name: categoryTranslations.name || {},
            description: categoryTranslations.description || {}
          }
        };
      } else {
        // Fallback to old system - dynamically build insert object
        const name = typeof nameOrTranslations === 'string' ? nameOrTranslations : nameOrTranslations.name.en || '';
        const insertData: any = {};
        
        if (schema.hasName) insertData.name = name;
        if (schema.hasGroupKey && groupKey) insertData.group_key = groupKey;
        if (schema.hasIcon && icon) insertData.icon = icon;
        if (schema.hasColor && color) insertData.color = color;
        if (schema.hasDescription && description) insertData.description = description;
        
        const { data, error } = await supabase
          .from("category_post")
          .insert(insertData)
          .select()
          .single();
        
        if (error) throw error;
        return data;
      }
    },

    // Update an existing post category (with fallback)
    async updatePostCategory(
      id: string,
      nameOrTranslations: string | { name: Record<LanguageCode, string>; description?: Record<LanguageCode, string> },
      groupKey?: string,
      icon?: string,
      color?: string,
      description?: string
    ): Promise<PostCategory> {
      const translationSystemAvailable = await checkTranslationSystemAvailable();
      const schema = await checkCategoryPostSchema();
      
      if (translationSystemAvailable && typeof nameOrTranslations === 'object') {
        // New translation system - only update non-translatable fields
        const updateData: any = {};
        if (schema.hasGroupKey && groupKey !== undefined) updateData.group_key = groupKey;
        if (schema.hasIcon && icon !== undefined) updateData.icon = icon;
        if (schema.hasColor && color !== undefined) updateData.color = color;
        
        const { data, error } = await supabase
          .from("category_post")
          .update(updateData)
          .eq("id", id)
          .select()
          .single();
        
        if (error) throw error;
        
        const translations = nameOrTranslations;
        const translationInputs: TranslationInput[] = [];
        
        Object.entries(translations.name).forEach(([lang, value]) => {
          translationInputs.push({
            field_name: 'name',
            language_code: lang as LanguageCode,
            value: value.trim()
          });
        });
        
        if (translations.description) {
          Object.entries(translations.description).forEach(([lang, value]) => {
            translationInputs.push({
              field_name: 'description',
              language_code: lang as LanguageCode,
              value: value.trim()
            });
          });
        }
        
        if (translationInputs.length > 0) {
          await translationHelpers.setRecordTranslations('category_post', data.id, translationInputs);
        }
        
        const categoryTranslationsData = await translationHelpers.getRecordTranslations('category_post', data.id);
        const categoryTranslations = translationHelpers.formatTranslations(categoryTranslationsData);
        
        return {
          ...data,
          name: translationHelpers.getLocalizedText(categoryTranslations.name, 'pl'),
          description: translationHelpers.getLocalizedText(categoryTranslations.description, 'pl'),
          translations: {
            name: categoryTranslations.name || {},
            description: categoryTranslations.description || {}
          }
        };
      } else {
        // Fallback to old system - dynamically build update object
        const name = typeof nameOrTranslations === 'string' ? nameOrTranslations : nameOrTranslations.name.en || '';
        const updateData: any = {};
        
        if (schema.hasName) updateData.name = name;
        if (schema.hasGroupKey && groupKey !== undefined) updateData.group_key = groupKey;
        if (schema.hasIcon && icon !== undefined) updateData.icon = icon;
        if (schema.hasColor && color !== undefined) updateData.color = color;
        if (schema.hasDescription && description !== undefined) updateData.description = description;
        
        const { data, error } = await supabase
          .from("category_post")
          .update(updateData)
          .eq("id", id)
          .select()
          .single();
        
        if (error) throw error;
        return data;
      }
    },

    // Create a new post with categories
    async createPost(postData: CreatePostData): Promise<Post> {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const { data: post, error: postError } = await supabase
        .from("post")
        .insert({
          title: postData.title,
          content: postData.content,
          is_public: postData.is_public,
          user_id: user.id
        })
        .select()
        .single();
      
      if (postError) throw postError;

      if (postData.category_ids && postData.category_ids.length > 0) {
        const categoryRelations = postData.category_ids.map(categoryId => ({
          post_id: post.id,
          category_id: categoryId
        }));

        const { error: relationError } = await supabase
          .from("post_category")
          .insert(categoryRelations);
        
        if (relationError) throw relationError;
      }

      return post;
    },

    // Update a post and its categories
    async updatePost(id: string, postData: UpdatePostData): Promise<Post> {
      const { data: post, error: postError } = await supabase
        .from("post")
        .update({
          title: postData.title,
          content: postData.content,
          is_public: postData.is_public
        })
        .eq("id", id)
        .select()
        .single();
      
      if (postError) throw postError;

      if (postData.category_ids !== undefined) {
        const { error: deleteError } = await supabase
          .from("post_category")
          .delete()
          .eq("post_id", id);
        
        if (deleteError) throw deleteError;

        if (postData.category_ids.length > 0) {
          const categoryRelations = postData.category_ids.map(categoryId => ({
            post_id: id,
            category_id: categoryId
          }));
          
          const { error: relationError } = await supabase
            .from("post_category")
            .insert(categoryRelations);
          
          if (relationError) throw relationError;
        }
      }

      return post;
    },

    // Delete a post
    async deletePost(id: string): Promise<void> {
      const { error } = await supabase
        .from("post")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },

    // Delete a post category
    async deletePostCategory(id: string): Promise<void> {
      const translationSystemAvailable = await checkTranslationSystemAvailable();
      
      if (translationSystemAvailable) {
        await translationHelpers.deleteRecordTranslations('category_post', id);
      }
      
      const { error } = await supabase
        .from("category_post")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },

    // Get posts by category
    async getPostsByCategory(categoryId: string): Promise<PostWithCategories[]> {
      const { data, error } = await supabase
        .from("post")
        .select(`
          *,
          post_category!inner(
            category_post(
              id
            )
          )
        `)
        .eq("post_category.category_id", categoryId)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      
      return data.map((post: any) => ({
        ...post,
        category_names: post.post_category?.map((pc: any) => pc.category_post.id) || []
      }));
    },

    // Get posts by category group key
    async getPostsByCategoryGroupKey(categoryGroupKey: string): Promise<PostWithCategories[]> {
      const { data, error } = await supabase
        .from("post")
        .select(`
          *,
          post_category!inner(
            category_post!inner(
              id
            )
          )
        `)
        .eq("post_category.category_post.group_key", categoryGroupKey)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      
      return data.map((post: any) => ({
        ...post,
        category_names: post.post_category?.map((pc: any) => pc.category_post.id) || []
      }));
    }
  };
};
